import PageBrand from '@components/common/PageBrand'
import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Pagination from '@mui/material/Pagination'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { useHistory } from 'react-router-dom'
import { GlassCard, GlassOutlineButton, GlassPanel } from '@components/common/Glass'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import Alert, { AlertInput } from '@components/common/Alert'
import BackgroundAnimation from '@components/common/BackgroundAnimation'
import {
  AdminListReport,
  BannedUserRow,
  getAdminReports,
  getBannedUsers,
  resolveAdminReport,
  setUserListCreatePermission,
} from 'src/api/admin'
import { getUser } from 'src/api/user'
import AppContext from 'src/app/AppContext'
import {
  altColor,
  altColorDarkText,
  altColorLighter,
  helperTextColor,
  mainColor,
  mainColorLighter,
  shadow1,
} from 'src/ui/theme/tokens'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
const Admin = (): JSX.Element => {
  const history = useHistory()
  const { appState } = React.useContext(AppContext)
  const [ready, setReady] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [reports, setReports] = useState<AdminListReport[]>([])
  const [reportsTotal, setReportsTotal] = useState(0)
  const [bannedUsers, setBannedUsers] = useState<BannedUserRow[]>([])
  const [status, setStatus] = useState<'open' | 'resolved'>('open')
  const [page, setPage] = useState(0)
  const pageSize = 20
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState<AlertInput>({ open: false, message: '' })
  const [userToUnban, setUserToUnban] = useState('')

  const inputSx = useMemo(
    () => ({
      '& .MuiInputBase-root': {
        background: '#ffffff',
        borderRadius: '14px',
        boxShadow: shadow1,
      },
      '& .MuiInputBase-input': { py: 1.25 },
    }),
    []
  )

  const loadData = React.useCallback(async () => {
    setLoading(true)
    const [reportsRes, bannedRes] = await Promise.all([
      getAdminReports(status, pageSize, page),
      getBannedUsers(),
    ])
    setLoading(false)
    if (reportsRes.success && reportsRes.data) {
      setReports(reportsRes.data.rows || [])
      setReportsTotal(Number(reportsRes.data.total || 0))
    } else
      setAlert({
        open: true,
        severity: 'error',
        message: reportsRes.message || 'Failed to load reports.',
      })

    if (bannedRes.success && bannedRes.data) setBannedUsers(bannedRes.data)
    else
      setAlert({
        open: true,
        severity: 'error',
        message: bannedRes.message || 'Failed to load banned users.',
      })
  }, [page, status])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(0)
  }, [status])

  useEffect(() => {
    const userId = appState.user?.id
    if (!userId) {
      history.replace('/login')
      return
    }
    getUser(userId).then((res) => {
      if (!res.success || !res.data) {
        history.replace('/')
        return
      }
      const nextIsAdmin = res.data.role === 'admin' || res.data.role === 'superadmin'
      setIsAdmin(nextIsAdmin)
      setReady(true)
      if (!nextIsAdmin) {
        history.replace('/')
      }
    })
  }, [appState.user?.id, history])

  useEffect(() => {
    if (!ready || !isAdmin) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadData()
  }, [ready, isAdmin, loadData])

  const resolve = async (
    reportId: number,
    action: 'none' | 'hide' | 'delete' | 'block' | 'accept',
    nextStatus: 'resolved'
  ) => {
    const res = await resolveAdminReport({
      reportId,
      status: nextStatus,
      resolutionAction: action,
    })
    if (!res.success) {
      setAlert({
        open: true,
        severity: 'error',
        message: res.message || 'Failed to resolve report.',
      })
      return
    }
    setAlert({ open: true, severity: 'success', message: 'Report updated.' })
    loadData()
  }

  const handleUnban = async () => {
    const id = Number(userToUnban || 0)
    if (!id) return
    const res = await setUserListCreatePermission(id, true)
    if (!res.success) {
      setAlert({
        open: true,
        severity: 'error',
        message: res.message || 'Failed to unban user.',
      })
      return
    }
    setAlert({
      open: true,
      severity: 'success',
      message: `User ${id} unbanned.`,
    })
    setUserToUnban('')
    loadData()
  }

  if (!ready) return <div />

  return (
    <div>
      <BackgroundAnimation />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <PageBrand />
        <GlassCard
          sx={{
            px: {
              xs: 1,
              sm: 2,
              md: 2.5,
            },
            py: {
              xs: 2.5,
              sm: 3,
            },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} gap={2}>
            <GlassOutlineButton
              variant="outlined"
              onClick={() => history.push('/')}
              sx={{ p: 1, px: 1, minWidth: 'auto' }}
            >
              <ArrowBackIosNewRoundedIcon fontSize="small" sx={{ mr: 0.25 }} />
            </GlassOutlineButton>
            <Typography variant="h5" fontWeight={800}>
              Moderation
            </Typography>
            <Box width={66} />
          </Box>

          <GlassPanel
            mt={2}
            p={1}
            sx={{
              backgroundColor: altColorLighter,
            }}
          >
            <Typography variant="h6" fontWeight={800}>
              Reports
            </Typography>
            <Stack spacing={1} mt={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Select
                  fullWidth
                  size="small"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as typeof status)}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                  }}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
                <Box display="flex" justifyContent="flex-end" alignItems="center">
                  <IconButton
                    onClick={loadData}
                    disabled={loading}
                    sx={{ px: 1, borderRadius: '16px' }}
                  >
                    <RefreshRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {reports.map((report) => (
                <GlassCard key={report.id} sx={{ p: 1.5 }}>
                  <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {report.contentType === 'poll' ? 'Poll' : 'List'} #{report.contentId} •{' '}
                      {report.status}
                    </Typography>
                  </Box>
                  <Box
                    mt={1}
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    {report.resolutionAction && report.status === 'resolved' ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={
                          report.resolutionAction === 'accept'
                            ? 'success'
                            : report.resolutionAction === 'none' ||
                                report.resolutionAction === 'hide'
                              ? 'warning'
                              : 'error'
                        }
                        sx={{
                          backgroundColor:
                            report.resolutionAction === 'accept'
                              ? '#EDF7EE'
                              : report.resolutionAction === 'none' ||
                                  report.resolutionAction === 'hide'
                                ? '#FFF4E6'
                                : '#fff0f1',
                          color:
                            report.resolutionAction === 'accept'
                              ? '#156451'
                              : report.resolutionAction === 'none' || report.resolutionAction === 'hide'
                                ? '#735119'
                                : '#a73743',
                          borderColor:
                            report.resolutionAction === 'accept'
                              ? '#b7ded2'
                              : report.resolutionAction === 'none' || report.resolutionAction === 'hide'
                                ? '#eddaa5'
                                : '#f1c2c6',
                        }}
                        label={
                          <Typography variant="caption" display="block" color="inherit">
                            <strong>Action:</strong> {report.resolutionAction}
                          </Typography>
                        }
                      />
                    ) : null}

                    <Chip
                      size="small"
                      variant="outlined"
                      sx={{
                        backgroundColor: mainColorLighter,
                        borderColor: mainColor,
                      }}
                      label={
                        <Typography variant="caption">
                          <strong>Creator:</strong> {report.creatorUsername || 'unknown'}{' '}
                          <Typography
                            variant="caption"
                            sx={{ fontSize: 10 }}
                            color={helperTextColor}
                          >
                            (#
                            {report.creatorId || 'n/a'})
                          </Typography>
                        </Typography>
                      }
                    />

                    <Chip
                      size="small"
                      variant="outlined"
                      sx={{
                        backgroundColor: altColorLighter,
                        borderColor: altColor,
                        color: altColorDarkText,
                      }}
                      label={
                        <Typography variant="caption">
                          <strong>Reporter:</strong> {report.reportedByUsername || 'unknown'}{' '}
                          {report.reportedByUserId ? (
                            <Typography
                              variant="caption"
                              sx={{ fontSize: 10 }}
                              color={helperTextColor}
                            >
                              #{report.reportedByUserId || 'n/a'}
                            </Typography>
                          ) : null}
                        </Typography>
                      }
                    />
                  </Box>
                  <Typography variant="body2" display="block" mt={0.25} textAlign="left" p={1}>
                    <strong>Reason:</strong> {report.reason}
                    {report.details ? ` • ${report.details}` : ''}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body1" mt={0.25} fontWeight={700}>
                    {report.title || '(untitled)'}
                  </Typography>
                  {report.items?.length ? (
                    <Box mt={0.75}>
                      {report.items.map((title, idx) => (
                        <Typography
                          key={`${report.id}-item-${idx}`}
                          variant="caption"
                          display="block"
                        >
                          {idx + 1}. {title}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                    mt={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => resolve(report.id, 'delete', 'resolved')}
                        color="error"
                      >
                        Delete
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => resolve(report.id, 'block', 'resolved')}
                        color="error"
                      >
                        Block
                      </Button>
                    </Box>

                    <Box flexGrow={1}></Box>

                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                      <Button
                        size="small"
                        onClick={() => {
                          const targetId = Number(report.contentId || 0)
                          if (!targetId) return
                          const href =
                            report.contentType === 'poll' ? `/polls/${targetId}` : `/?l=${targetId}`
                          window.open(href, '_blank', 'noopener,noreferrer')
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={0.5}>
                          View
                          <OpenInNewRoundedIcon fontSize="inherit" sx={{ fontSize: 16 }} />
                        </Box>
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={() => resolve(report.id, 'hide', 'resolved')}
                      >
                        Hide
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => resolve(report.id, 'accept', 'resolved')}
                      >
                        Accept
                      </Button>
                    </Box>
                  </Box>
                </GlassCard>
              ))}
              {!reports.length ? (
                <Typography variant="body1" py={2} fontWeight={500}>
                  No reports for this filter.
                </Typography>
              ) : null}
            </Stack>
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                page={page + 1}
                count={Math.max(1, Math.ceil(reportsTotal / pageSize))}
                onChange={(_e, value) => setPage(Math.max(0, value - 1))}
                shape="rounded"
                color="primary"
                disabled={loading}
              />
            </Box>
          </GlassPanel>

          <GlassPanel mt={3} sx={{ p: 1, backgroundColor: altColorLighter }}>
            <Typography variant="h6" fontWeight={600}>
              Banned Users
            </Typography>
            <Box mt={1} display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                sx={inputSx}
                label="User ID to unban"
                value={userToUnban}
                onChange={(e) => setUserToUnban(e.target.value.replace(/[^0-9]/g, ''))}
              />
              <Button onClick={handleUnban}>
                Unban
              </Button>
            </Box>
            <TableContainer sx={{ mt: 1, overflowX: 'auto' }}>
              <Table size="small" sx={{ mt: 1, backgroundColor: 'white', borderRadius: '8px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bannedUsers.map((row) => (
                    <TableRow key={`banned-${row.id}`}>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.username}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.role}</TableCell>
                    </TableRow>
                  ))}
                  {!bannedUsers.length ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 4, fontWeight: 500 }}>
                        No banned users.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </GlassPanel>
        </GlassCard>
      </Container>
      <Alert alert={alert} onClose={() => setAlert((prev) => ({ ...prev, open: false }))} />
    </div>
  )
}

export default Admin
